import { create } from "zustand";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "lockcal.theme";

function isMode(value: unknown): value is ThemeMode {
  return value === "system" || value === "light" || value === "dark";
}

/** The theme chosen on this device. Used before an account is signed in. */
export function getStoredTheme(): ThemeMode {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isMode(value) ? value : "system";
  } catch {
    return "system";
  }
}

function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  return prefersDark() ? "dark" : "light";
}

/** Paints the document. Safe to call on every change. */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(mode);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.dataset.theme = resolved;

  const color = resolved === "dark" ? "#121211" : "#ffffff";
  // index.html ships two media-scoped metas so the browser chrome matches the
  // OS preference before React runs. Once the user picks a theme that no
  // longer follows the OS, collapse them into one explicit value.
  const metas = Array.from(
    document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'),
  );
  if (metas.length === 0) {
    const meta = document.createElement("meta");
    meta.name = "theme-color";
    meta.content = color;
    document.head.appendChild(meta);
    return;
  }
  metas.forEach((meta, index) => {
    if (index === 0) {
      meta.removeAttribute("media");
      meta.setAttribute("content", color);
    } else {
      meta.remove();
    }
  });
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  /** Adopt the value stored on the server without writing it back. */
  adopt: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  mode: getStoredTheme(),

  setMode(mode) {
    set({ mode });
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Private mode / storage disabled: the theme just will not persist.
    }
    applyTheme(mode);
  },

  adopt(mode) {
    if (getStoredTheme() === mode) return;
    set({ mode });
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
    applyTheme(mode);
  },
}));
